import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { Icon, Popup, Button, List, Segment } from 'semantic-ui-react'
import moment from 'moment-timezone'

import db_lib from 'xAppLib/libs/db_lib';
import {Duration} from "../xAppLib/UIelems/Duration";
import API_service from 'xAppLib/providers/API_service'
import {DataShow} from "../xAppLib/DataTable";
import scripts_list_model from 'models/scripts_list_model'

import Patient from 'views/patients/Patient'
import {from_melb_ui_tm, UI_DATETIME_FMT} from "../helpers/datetime";
import logger from 'xAppLib/libs/logger';
import group_by_val_obj from "../xAppLib/helpers/group_by_val_obj";
import { AnswersList } from "../xAppLib/Users/AnswersList";
import { AnswerFlag } from "../xAppLib/Users/AnswerFlag";
import { flagged_answers } from "../views/med/utils";
import Photos from 'xAppLib/Photos/Photos';
import { TYPE_DICT } from "./treatment_plan_model";

const _FRDB_LOC = 'MedScripts';

const DEBUG = false

function RecentAppScripts({list}) {
	const SHOW_MORE = 5;
	const [showAll, setShowAll] = useState(false);
	const scripts = Object.values(list);

	const oldest_first = scripts.sort((x, y) => y.tm.localeCompare(x.tm));

	const filtered = showAll
		? oldest_first
		: oldest_first.slice(0, SHOW_MORE);

	const by_date = group_by_val_obj(filtered, scr => scr.tm.split('T')[0]);

	return (
		<Segment size="small">
			<div className="flex justify-between items-center">
				<b>Recent Activity ({scripts.length})</b>
				{scripts.length > SHOW_MORE && (
					<Button type="button"
							basic
							color="black"
							size="mini"
							onClick={() => setShowAll(x => !x)}
					>
						{showAll ? 'Less' : 'More'}
					</Button>
				)}
			</div>
			<List divided>
				{Object.keys(by_date).map(dt => (
					<List.Item key={dt}>
						<List.Content>
							<List.Header>
								<small className="font-normal">{moment(dt).format('DD/MM/YYYY')}</small>
							</List.Header>

							{by_date[dt].map(scr => (
								<div key={scr.sid}>
									<i>{![undefined, 'doccons'].includes(scr.script_type) && ![undefined, 'medcert'].includes(scr.req_type) && (scripts_list_model.TYPE_DICT(scr.script_type, scr.req_type)+' - ')}</i> 
									<b>{scr.med}</b> <small>{scr.sz} {scr.qnty}</small>
								</div>
							))}
						</List.Content>
					</List.Item>
				))}
			</List>
		</Segment>
	);
}

function ActiveTreatmentPlans({list}) {
	const SHOW_MORE = 5;
	const [showAll, setShowAll] = useState(false);

	const plans = Object.values(list);

	const filtered = showAll
		? plans
		: plans.slice(0, SHOW_MORE);

	return (
		<Segment size="small">
			<div className="flex justify-between items-center">
				<b>Active Treatment Plans ({plans.length})</b>
				{plans.length > SHOW_MORE && (
					<Button type="button"
							basic
							color="black"
							size="mini"
							onClick={() => setShowAll(x => !x)}
					>
						{showAll ? 'Less' : 'More'}
					</Button>
				)}
			</div>
			<List divided>
				{filtered.map(plan => (
					<List.Item key={plan.tpid}>
						<List.Content className="whitespace-nowrap">
							<i>{TYPE_DICT[plan.type] || plan.type}</i> -{" "}
							<b>{plan.med.name}</b> <small>{plan.med.size} {plan.med.qnty}</small>{" "}
							({plan.used} of {plan.total} used)
						</List.Content>
					</List.Item>
				))}
			</List>
		</Segment>
	);
}

function FlaggedAnswers({answs, title = "Flagged Answers", popup}) {
	const flags = useMemo(() => Array.from(new Set(flagged_answers(answs))), [answs]);
	if (flags.length === 0) return null;

	return (
		<Segment size="small">
			<div className="flex justify-between items-center">
				<b className="whitespace-nowrap mr-2">{title}</b>
				{popup}
			</div>
			<List>
				{flags.map(flg_id => <List.Item key={flg_id}><AnswerFlag flg_id={flg_id}/></List.Item>)}
			</List>
		</Segment>
	);
}

function MustReview() {
	return (
		<Segment color="red" className="text-red-700 whitespace-nowrap">
			<Icon name="warning sign" color="red"/> You must review before approving
		</Segment>
	);
}

function ReviewSection({row}) {
	const sections = [
		row.must_review && <MustReview key="must-review" />,
		flagged_answers(row.answs).length && <FlaggedAnswers key="flagged-answers" answs={row.answs}/>,
		flagged_answers(row.prev_flg_ans_scr?.answs).length && (
			<FlaggedAnswers key="prev-flagged-answers" answs={row.prev_flg_ans_scr?.answs} title="Recent Flagged Answers" popup={<Popup
				trigger={<Button icon='question' compact/>}
				content={<>
					<p>Flagged answers from a previous form submission for the same medication:</p>
					<AnswersList answs={row.prev_flg_ans_scr?.answs}/>
				</>}
				wide='very'
				on={['click']}
			/>}/>
		),
		row.recent_app_scripts && <RecentAppScripts key="recents" list={row.recent_app_scripts}/>,
		row.active_treatment_plans && <ActiveTreatmentPlans key="active-treatment-plans" list={row.active_treatment_plans}/>,
	].filter(Boolean);

	if (sections.length) {
		return (
			<Segment.Group>
				{sections}
			</Segment.Group>
		);
	}

	return null;
}



const _LIST_DET_FIELDS = [

	{
		name: 'Wait Time',
		type: 'compound',
		parts : [
			{
				name: 'add_tm',
				jpath: 'add_tm'
			},
			{
				name: 'hist',
				jpath: 'hist'
			},
			{
				name: 'script_type',
				jpath: 'script_type'
			},
			{
				name: 'req_type',
				jpath: 'req_type'
			},
			{
				name: 'rev_tm',
				jpath: 'rev_tm'
			},
		],
		template: ({add_tm, hist, script_type, req_type, rev_tm}) => {
			if (!add_tm) {
				return null;
			}
			// TODO duplication with cosm

			const transitions = Object.values(hist || {})
				.filter(h => h.tm && h.to?.status)
				.sort((x, y) => x.tm - y.tm)
				.map(h => ({ status: h.to.status, tm: h.tm }));

			const first_entered_wr = from_melb_ui_tm(add_tm);
			const last_entered_wr = transitions
				.filter(h => h.status === 'instcons_await')
				.slice(-1)[0];

			const consult_taken = transitions
				// If they were put back into WR, make sure we're looking after that time
				.filter(h => h.status === 'instcons_taken' && h.tm > (last_entered_wr?.tm || 0))
				.slice(-1)[0];

			const consult_finished = transitions
				// Unlikely, but if the consult was approved/rejected then sent back to WR this
				// ensures we're only finding the last transition after that time
				.filter(h => /^instcons_(approved|rejected|cancel)$/.test(h.status) && h.tm > ((consult_taken || last_entered_wr)?.tm || 0))
				.slice(-1)[0];

			// Covers the case where the dr instant approved/rejects the consult, i.e. bypassing
			// consult taken. In this situation the consult time will be zero
			const action_taken = (consult_taken || consult_finished);

			return <>
					<div className="pl-6">
						{first_entered_wr.format(UI_DATETIME_FMT)}
					</div>
					<div className="pl-6">
						<b>{scripts_list_model.TYPE_DICT(script_type, req_type)}</b>
					</div>

					<div style={{ fontFamily: "monospace"}}>
						<Duration icon live={!action_taken}
								  start={first_entered_wr}
								  end={action_taken?.tm}
								  iconColor={d => (
									  d.asMinutes() <= 2 && 'grey'
									  || d.asMinutes() <= 3 && 'green'
									  || d.asMinutes() <= 5 && 'blue'
									  || d.asMinutes() <= 8 && 'yellow'
									  || d.asMinutes() <= 12 && 'orange'
									  || 'red'
								  )}						
									/>
					</div>

					{rev_tm && (app.acl.is_admin || app.acl.is_supp) && (
						<div style={{ fontFamily: 'monospace' }}>
							<Duration 
								live
								icon="doctor" 
								start={moment(rev_tm)}
								iconColor={d => d.asMinutes() >= 30 ? 'red' : 'grey'} 
							/>
						</div>
					)}

				{consult_taken && 
					<div className="pl-6">
						{moment(consult_taken.tm).format(UI_DATETIME_FMT)}
					</div>
				}
				{action_taken && (<>
					{action_taken.status !== 'instcons_cancel' && (
						<div style={{ fontFamily: "monospace"}}>
							<Duration icon={'doctor'}
									  live={!consult_finished}
									  start={action_taken.tm}
									  end={consult_finished?.tm}
									  iconColor={d => d.asMinutes() < 1 && 'green' || 'blue'}
							/>
						</div>
					)}
					<div className="pl-6">
						{consult_finished && moment(consult_finished.tm).format(UI_DATETIME_FMT)}
					</div>
				</>)}
			</>;
		}
	},

	{
		name: 'Patient',
		type: 'compound',
		parts : [
			{
				name: 'First',
				jpath: 'spd_data.first_name'
			},
			{
				name: 'Last',
				jpath: 'spd_data.last_name'
			},
			{
				name: 'DOB',
				jpath: 'spd_data.dob',
				type: 'age',
			},
			{
				name: 'Email',
				jpath: 'spd_data.email',
				type: 'show_more',
				cut_length: 20,
			},
			{
				name: 'Sex',
				jpath: 'spd_data.sex',
			},
			{
				name: 'Mobile',
				jpath: 'spd_data.mobile',
			},
			{
				name: 'Medicare',
				jpath: 'spd_data.medicare'
			},
			{
				name: 'Conc',
				jpath: 'spd_data.conc_card'
			},

			{
				name: 'user_iding',
				jpath: 'user_iding',
			},
			{
				name: 'ihi_num',
				jpath: 'ihi_num',
			},
			{
				name: 'pts_uid',
				jpath: 'pts_uid',
			},
			{
				name: 'mc_vf',
				jpath: 'meta.medicare_verified',
			},
			{
				name: 'cn_vf',
				jpath: 'meta.concession_verified',
			},
			{name:'mdcr_first_name',jpath: 'spd_data.mdcr_first_name'},
			{name:'mdcr_last_name',jpath: 'spd_data.mdcr_last_name'},

			{ name: 'n__type', jpath: 'n__type',
				type: 'or',	jpath1: 'notes.type' },
			{ name: 'n__lvl', jpath: 'n__lvl',
				type: 'or',	jpath1: 'notes.lvl' },
			{ name: 'n__cont', jpath: 'n__cont',
				type: 'or',	jpath1: 'notes.cont' },
			{ name: 'n__desc', jpath: 'n__desc',
				type: 'or',	jpath1: 'notes.desc' },
			{ name: 'n__cre_by', jpath: 'n__cre_by',
				type: 'or',	jpath1: 'notes.cre_by' },
			{ name: 'n__cre_tm', jpath: 'n__cre_tm',
				type: 'or',	jpath1: 'notes.cre_tm' },
		],
		template : ({First,Last,DOB,Email,Sex,Mobile,Medicare, Conc, pts_view_link, mc_vf, cn_vf,mdcr_first_name,mdcr_last_name, n__type, n__lvl, n__cont, n__desc, n__cre_by, n__cre_tm, user_iding, ihi_num, pts_uid})=> <React.Fragment>
							<Link to = {`/patient/dp/${btoa(`d=${user_iding}&i=${ihi_num}&u=${pts_uid}`)}`}>
								<Icon name = {'search'} />
							</Link>
							<Popup
								content={
									<Patient
										embedded
										by_par="ui"
										by_id={user_iding}
										by_uid={pts_uid}
										by_ihi={ihi_num}
										hide_act_btns
									/>
								}
								on='click'
								position="right center"
								trigger={<Icon name = {'comment alternate outline'} />}
								style={{maxHeight:'60vh',overflow:'scroll',width:'40vw'}}
								/>

							&nbsp;
							{First} {Last} 
							&nbsp;
							{n__type && n__lvl && 
								<Popup 
									trigger={ <Icon 
													name={n__lvl=='info' && "info circle" || n__lvl=='warn' && "warning circle"  || n__lvl=='consult' && "doctor" || n__lvl=='alert' && "warning sign" || 'flag checkered'} 
													color={n__lvl=='info' && "green" || n__lvl=='warn' && "yellow" || n__lvl=='consult' && "blue" || n__lvl=='alert' && "red" || 'grey'} 
												/>} 
									>
									<Icon 
													name={n__lvl=='info' && "info circle" || n__lvl=='warn' && "warning circle"  || n__lvl=='consult' && "doctor" || n__lvl=='alert' && "warning sign" || 'flag checkered'} 
													color={n__lvl=='info' && "green" || n__lvl=='warn' && "yellow" || n__lvl=='consult' && "blue" || n__lvl=='alert' && "red" || 'grey'} 
												/>
									<b>{n__cont}</b><br />
									{n__desc}<br />
									<i>by {n__cre_by.n} on {moment(n__cre_tm).tz("Australia/Melbourne").format('D/M/YY')}</i>
								</Popup>
							}
							<br/>
							{mdcr_first_name && <React.Fragment>({mdcr_first_name} {mdcr_last_name}) <Popup content='Real name confirmed by Medicare' trigger={ <Icon name="question circle outline"/>} /><br/></React.Fragment>}
							{Sex && <Icon name={Sex=='F' && 'woman' || Sex=='M' && 'man' || 'question'} color={Sex=='F' && 'pink' || Sex=='M' && 'blue' || 'green'} />}
							{DOB} 

							<br/>
							{Mobile && <a href={`tel:${Mobile.replace(/\s/g,'')}`}><Icon name='phone' />{Mobile}</a>}
						</React.Fragment>
	},

	{
		name: 'Medication',
		type: 'compound',
		_id: 'treatment',
		parts: [
			{
				name: 'Medicine',
				jpath: 'med_db_data.name',
				type: 'show_more',
				cut_length: 50,
			},
			{
				name: 'Size',
				jpath: 'med_db_data.size',
				type: 'show_more',
				cut_length: 15,
			},
			{
				name: 'med_db_data',
				jpath: 'med_db_data',
			},
			{
				name: 'Quantity',
				jpath: 'cosm_det.qua',
				type: 'show_more'
			},
			{
				name: 'Location',
				jpath: 'cosm_det.loc',
				type: 'show_more'
			},
			{
				name: 'Notes',
				jpath: 'cosm_det.note',
				type: 'show_more'
			},

			{
				name: 'send_to',
				jpath: 'med_db_data.send_to',
				type: 'show_more',
				cut_length: 20,
			},
			{
				name: 'med_items',
				jpath: 'med_db_data.items',
				// type: 'show_more',
				// cut_length: 20,
			},
			{
				name: 'med_req_txt',
				jpath: 'med_db_data.req_txt',
				// type: 'show_more',
				// cut_length: 20,
			},
			{
				name: 'others',
				jpath: 'others'
			},
			{
				name: 'last_consult',
				jpath: 'last_consult'
			},
			{
				name: 'form_data',
				jpath: 'form_data'
			},
			{
				name:'note',
				jpath:'note'
			},

			{
				name: 'cons_allergy',
				jpath: 'form_data.cons_allergy',
				type: 'show_more',
				cut_length: 25,
			},
			{
				name: 'cons_meds',
				jpath: 'form_data.cons_meds',
				type: 'show_more',
				cut_length: 25,
			},			
		],
		template : ({row, Medicine,Size,Quantity,Location,Notes, send_to, note, med_db_data, form_data, med_req_txt, others, is_review, last_consult, cons_allergy, cons_meds})=> {
			return <React.Fragment>
				<p className="text-xl">
					<b>{Medicine}</b> {Size} {med_db_data?.qnty}<br/>
					{Quantity && <React.Fragment>Quantity: {Quantity}<br/></React.Fragment>}
					{Location && <React.Fragment>Location: {Location}<br/></React.Fragment>}
					{Notes && <React.Fragment>Notes: {Notes}</React.Fragment>}
					{send_to && <>send to: {send_to}<br/></> ||''}
					{med_req_txt && <b><br/><li>{med_req_txt}</li></b>||''}
					{form_data?.cons_desc && <>Patient note: <b>{form_data.cons_desc}</b><br/></>}
					{form_data?.cons_medhist && <>Medical history: {form_data.cons_medhist}<br/></>}
					{form_data?.cons_allergy && <>Allergy: {form_data.cons_allergy}<br/></>}
	 				{form_data?.cons_meds && <>Current Meds: {form_data.cons_meds}<br/></>}
 				</p>
				
				{(others || []).map((other) => (
					<div key={other.sid}>
						<hr className="my-2"/>
						{DataShow.show_data_field(other, _LIST_DET_FIELDS.find(x => x._id === 'treatment'))}
					</div>
				))}
				{note && <em>Note: {note}</em>}

				{row.form_data.phts && <>
							<Photos
									size={'calc(100% / 4 - 2em)'}
									data = {row.form_data.phts || []}
									mode = 'view_only'
							 />
						</>}
				<ReviewSection row={row} />
			</React.Fragment>
		}
	},
	{
		name: 'Answers',
		type: 'compound',

		parts : [
			{
				name: 'Answers',
				jpath: 'answs',
			},
		],
		template : ({Answers})=> {
				return <React.Fragment>
						<Popup
							position="bottom right"
							trigger={<Button icon='question' compact/>}
							content={<AnswersList answs={Answers} />}
							wide='very'
							on={['click']}
							/>
					</React.Fragment>
			}
	},
];

function owner() { return {uid:app.user.uid,name:app.user.name} }

export default class instcons_global_model extends db_lib {

	static get FRDB_LOC() { return _FRDB_LOC; }
	static get LIST_DET_FIELDS() { return _LIST_DET_FIELDS; }

	static watch_approvals(onData, onError) {
		return this.watch_all_records(onData, {where_key: 'status', where_val: 'auto_approved_delay', ce: onError });
	}

	static watch_urgent_fu(onData, onError) {
		return this.watch_all_records(onData, {where_key: 'wr', where_val: 'urgent_fu', ce: onError });
	}

	static watch_unseen_results (doc_id, onData, onError) {
		return this.watch_all_records(onData, { where_key: 'unseen_results/doc_id', where_val: doc_id, ce: onError });
	}

	static watch_unseen_results_express (doc_id, onData, onError) {
		return this.watch_all_records(onData, { where_key: 'unseen_results_express/doc_id', where_val: doc_id, ce: onError });
	}

	static sort_med_scripts_for_wr(records) {
		return Object.values(records || {}).map(rec => ({ ...rec, add_ts: from_melb_ui_tm(rec.add_tm).valueOf() })).sort((x, y) => {
			// if they're both null or the same order value, sort oldest to newest
			if (x.ord === y.ord) {
				return x.add_ts - y.add_ts;
			}

			// put any unknown order cases to the end
			if (x.ord == null) return 1;
			if (y.ord == null) return -1;

			// otherwise sort by order value
			return x.ord - y.ord;
		});
	}

	static awaiting_exprcons(onData, onError) {
		return this.watch_all_records(records => onData(this.sort_med_scripts_for_wr(records)), {where_key: 'wr', where_val: 'exprcons', ce: onError });
	}
	
	static awaiting_gp_consult(onData, onError) {
		return this.watch_all_records(records => onData(this.sort_med_scripts_for_wr(records)), {where_key: 'wr', where_val: 'await_consult', ce: onError });
	}

	static async own(sid, own = true) {
		try {
			const rev_tm = own ? Date.now() : null;
			const getOwner = own ? owner() : null;
			await instcons_global_model.update_record(sid, { owner: getOwner, rev_tm });
		} catch (error) {
			console.log("got error",error);
		}
		
	}

	static create_session({sid, test} = {}) {
		return API_service.load_data('instcons/create_session', {sid, test})
	}


	static async update_status(row, status, p) {
		const { sid } = row
		try {
			const data = {
				pending:true,
				owner:owner(),
				doc: app.user.claims.doc_id
			} 
			await instcons_global_model.update_record(
				sid,
				data,
				{add_hist: true}
			);
			const chgStat = () => API_service.load_data( 'chgStat', {sid, stat: status, doc: app.user.claims.doc_id, ...p} )
			let ret
			try {
				ret = await chgStat()
			} catch (error) {
				DEBUG && console.error(error)
				logger.report_error('ERROR in WR update_status', 'error', error);
				try {
					ret = await chgStat()
				} catch (error) {
					DEBUG && console.error(error)
					logger.report_error('ERROR [TWICE] in WR update_status', 'error', error);
					ret = error
					instcons_global_model.update_record( sid,{
						pending:null,
						owner:null,
						doc:null,
						rev_tm: null
					},{add_hist: true})
				}
			}
			return ret
		   
		} catch (error) {
			console.log("got error updating status",error);
			logger.report_except(error,{sid,status,owner:owner()})
			return {res:'err',error}
		}
		
	}

}